import { useCallback, useRef, useState } from 'react';
import { expectJsonRecord, maybeJsonRecord } from '../runtime-validation';
// =============================================================================
// Hook implementation
// =============================================================================
/**
 * Maximum number of conversation items retained. When exceeded, the oldest
 * items are pruned. This prevents unbounded memory growth during long agent
 * sessions that generate thousands of tool calls, messages, and thinking
 * blocks — the primary cause of Chrome tab crashes.
 */
const MAX_CONVERSATION_ITEMS = 500;
/**
 * Maximum text length for a single agent message or thinking block.
 * Extremely long streaming responses (e.g., dumping a large file) are
 * truncated to prevent a single item from consuming excessive memory.
 */
const MAX_ITEM_TEXT_LENGTH = 512_000; // 512 KB
let itemCounter = 0;
function nextId() {
    return `item-${++itemCounter}-${Date.now()}`;
}
/**
 * Enforce the item cap on an array. Drops oldest items when the cap is exceeded.
 * Returns the same array reference if no pruning is needed.
 */
function enforceItemCap(items) {
    if (items.length <= MAX_CONVERSATION_ITEMS)
        return items;
    return items.slice(items.length - MAX_CONVERSATION_ITEMS);
}
/**
 * Update the last item in the array efficiently. When the last item matches
 * the predicate, returns a new array with only the last element replaced —
 * reuses the prefix via slice instead of spreading every element.
 */
function updateLastItem(prev, predicate, updater, fallback) {
    const last = prev[prev.length - 1];
    if (last && predicate(last)) {
        const result = prev.slice(0);
        result[result.length - 1] = updater(last);
        return result;
    }
    return enforceItemCap([...prev, fallback()]);
}
/**
 * Finalize any active streaming items (agent_message.streaming → false,
 * thinking.active → false). Only creates new objects for items that
 * actually need updating — skips the rest.
 */
function finalizeStreamingItems(prev) {
    let changed = false;
    const result = prev.map((item) => {
        if (item.kind === 'agent_message' && item.streaming) {
            changed = true;
            return { ...item, streaming: false };
        }
        if (item.kind === 'thinking' && item.active) {
            changed = true;
            return { ...item, active: false };
        }
        return item;
    });
    return changed ? result : prev;
}
/**
 * Hook that processes ACP session update messages into a structured conversation.
 * Maps SessionNotification.Update variants to ConversationItem types.
 *
 * Memory safety:
 * - Items are capped at MAX_CONVERSATION_ITEMS (oldest pruned)
 * - Individual message text is capped at MAX_ITEM_TEXT_LENGTH
 * - Streaming chunk updates reuse the array prefix to avoid O(n) copies
 * - Tool call updates target only the matching item by index
 *
 * No client-side persistence — on reconnect, the ACP agent replays the full
 * conversation via LoadSession, which sends session/update notifications
 * through the WebSocket. Call `clear()` before reconnection to avoid duplicates.
 */
export function useAcpMessages() {
    const [items, setItems] = useState([]);
    const [usage, setUsage] = useState({ inputTokens: 0, outputTokens: 0, totalTokens: 0 });
    const [availableCommands, setAvailableCommands] = useState([]);
    // Track the last tool call index for efficient tool_call_update lookups.
    // Most updates target the most recently added tool call, so searching
    // backward from here avoids scanning the entire array.
    const lastToolCallIndexRef = useRef(-1);
    const processMessage = useCallback((msg) => {
        if (isAgentCrashReport(msg)) {
            setItems((prev) => enforceItemCap([...prev, {
                    kind: 'agent_crash_report',
                    id: nextId(),
                    agentType: msg.agentType,
                    recovered: msg.recovered,
                    message: msg.message,
                    attribution: msg.attribution,
                    stderr: msg.stderr,
                    stderrTruncated: msg.stderrTruncated,
                    suggestion: msg.suggestion,
                    recoveryError: msg.recoveryError,
                    timestamp: Date.parse(msg.timestamp) || Date.now(),
                }]));
            return;
        }
        // Handle session notifications (method === 'session/update')
        if (msg.method === 'session/update' && msg.params) {
            const params = msg.params;
            const update = params.update;
            if (!update?.sessionUpdate)
                return;
            const now = Date.now();
            switch (update.sessionUpdate) {
                case 'agent_message_chunk': {
                    const content = update;
                    const text = content.content?.type === 'text' ? (content.content.text ?? '') : '';
                    setItems((prev) => updateLastItem(prev, (item) => item.kind === 'agent_message' && item.streaming, (item) => {
                        const am = item;
                        const newText = am.text.length + text.length > MAX_ITEM_TEXT_LENGTH
                            ? am.text // Silently stop appending when cap reached
                            : am.text + text;
                        return { ...am, text: newText };
                    }, () => ({ kind: 'agent_message', id: nextId(), text, streaming: true, timestamp: now })));
                    break;
                }
                case 'agent_thought_chunk': {
                    const content = update;
                    const text = content.content?.type === 'text' ? (content.content.text ?? '') : '';
                    setItems((prev) => updateLastItem(prev, (item) => item.kind === 'thinking' && item.active, (item) => {
                        const ti = item;
                        const newText = ti.text.length + text.length > MAX_ITEM_TEXT_LENGTH
                            ? ti.text
                            : ti.text + text;
                        return { ...ti, text: newText };
                    }, () => ({ kind: 'thinking', id: nextId(), text, active: true, timestamp: now })));
                    break;
                }
                case 'user_message_chunk': {
                    // User messages arrive here from two sources:
                    // 1. LoadSession replay (restoring conversation on reconnect)
                    // 2. Synthetic injection by the VM agent during live prompts
                    //    (so the replay buffer and Durable Object have user messages)
                    //
                    // In the live case, addUserMessage() has already added the message
                    // to the items list for instant UX. Deduplicate by checking if a
                    // recent user_message with matching text already exists.
                    const content = update;
                    const text = content.content?.type === 'text' ? (content.content.text ?? '') : '';
                    if (text) {
                        setItems((prev) => {
                            // Deduplicate: check last few items for a matching user message.
                            // The addUserMessage call happens right before session/prompt is
                            // sent, so the matching item is typically the last one or close.
                            for (let i = prev.length - 1; i >= Math.max(0, prev.length - 5); i--) {
                                const item = prev[i];
                                if (item.kind === 'user_message' && item.text === text) {
                                    return prev; // Already present — skip duplicate
                                }
                                // Stop scanning once we hit a non-user item (agent response
                                // or tool call means the user message is from a prior turn).
                                if (item.kind !== 'user_message')
                                    break;
                            }
                            return enforceItemCap([...prev, { kind: 'user_message', id: nextId(), text, timestamp: now }]);
                        });
                    }
                    break;
                }
                case 'tool_call': {
                    const tc = update;
                    // Finalize any streaming agent message or thinking block
                    setItems((prev) => {
                        const finalized = finalizeStreamingItems(prev);
                        const newItem = {
                            kind: 'tool_call',
                            id: nextId(),
                            toolCallId: tc.toolCallId ?? '',
                            title: tc.title ?? 'Tool Call',
                            toolKind: tc.kind,
                            status: tc.status ?? 'in_progress',
                            content: (tc.content ?? []).map(mapToolCallContent),
                            locations: tc.locations ?? [],
                            timestamp: now,
                        };
                        const result = enforceItemCap([...finalized, newItem]);
                        lastToolCallIndexRef.current = result.length - 1;
                        return result;
                    });
                    break;
                }
                case 'tool_call_update': {
                    const tcu = update;
                    setItems((prev) => {
                        // Search backward from the last known tool call index for efficiency.
                        // Most tool_call_updates target the most recent tool call.
                        let targetIdx = -1;
                        const startIdx = Math.min(lastToolCallIndexRef.current, prev.length - 1);
                        for (let i = startIdx; i >= 0; i--) {
                            const item = prev[i];
                            if (item.kind === 'tool_call' && item.toolCallId === tcu.toolCallId) {
                                targetIdx = i;
                                break;
                            }
                        }
                        // Fallback: search forward from startIdx+1 in case of pruning
                        if (targetIdx < 0) {
                            for (let i = startIdx + 1; i < prev.length; i++) {
                                const item = prev[i];
                                if (item.kind === 'tool_call' && item.toolCallId === tcu.toolCallId) {
                                    targetIdx = i;
                                    break;
                                }
                            }
                        }
                        if (targetIdx < 0)
                            return prev; // Not found — skip
                        const target = prev[targetIdx];
                        const updated = {
                            ...target,
                            status: tcu.status ?? target.status,
                            title: tcu.title ?? target.title,
                            content: tcu.content ? tcu.content.map(mapToolCallContent) : target.content,
                        };
                        const result = prev.slice(0);
                        result[targetIdx] = updated;
                        return result;
                    });
                    break;
                }
                case 'plan': {
                    const plan = update;
                    setItems((prev) => {
                        const existing = prev.findIndex((i) => i.kind === 'plan');
                        const planItem = {
                            kind: 'plan',
                            id: existing >= 0 ? (prev[existing]?.id ?? nextId()) : nextId(),
                            entries: (plan.entries ?? []).map((e) => ({
                                content: e.content,
                                priority: e.priority,
                                status: e.status,
                            })),
                            timestamp: now,
                        };
                        if (existing >= 0) {
                            const result = prev.slice(0);
                            result[existing] = planItem;
                            return result;
                        }
                        return enforceItemCap([...prev, planItem]);
                    });
                    break;
                }
                case 'available_commands_update': {
                    const commandUpdate = update;
                    if (commandUpdate.availableCommands) {
                        setAvailableCommands(commandUpdate.availableCommands.map((cmd) => ({
                            name: cmd.name,
                            description: cmd.description || '',
                            source: 'agent',
                        })));
                    }
                    break;
                }
                case 'usage_update': {
                    // Acknowledged ACP notification — context window stats (not rendered in chat)
                    break;
                }
                case 'config_option_update': {
                    // Acknowledged ACP notification: session selector state, not transcript content.
                    break;
                }
                default: {
                    // Unknown/unsupported update type — render as raw fallback
                    setItems((prev) => enforceItemCap([...prev, { kind: 'raw_fallback', id: nextId(), data: update, timestamp: now }]));
                    break;
                }
            }
            return;
        }
        // Handle prompt responses (result with stopReason)
        if (msg.result && typeof msg.result === 'object') {
            const result = msg.result;
            if (result.stopReason) {
                // Finalize any streaming items
                setItems(finalizeStreamingItems);
                // Update token usage
                if (result.usage) {
                    setUsage((prev) => ({
                        inputTokens: prev.inputTokens + (result.usage.inputTokens ?? 0),
                        outputTokens: prev.outputTokens + (result.usage.outputTokens ?? 0),
                        totalTokens: prev.totalTokens + (result.usage.totalTokens ?? 0),
                    }));
                }
            }
        }
    }, []);
    const addUserMessage = useCallback((text) => {
        setItems((prev) => enforceItemCap([...prev, {
                kind: 'user_message',
                id: nextId(),
                text,
                timestamp: Date.now(),
            }]));
    }, []);
    const clear = useCallback(() => {
        setItems([]);
        setUsage({ inputTokens: 0, outputTokens: 0, totalTokens: 0 });
        lastToolCallIndexRef.current = -1;
    }, []);
    const prepareForReplay = useCallback(() => {
        setItems([]);
        setUsage({ inputTokens: 0, outputTokens: 0, totalTokens: 0 });
        setAvailableCommands([]);
        lastToolCallIndexRef.current = -1;
    }, []);
    return { items, usage, availableCommands, processMessage, addUserMessage, clear, prepareForReplay };
}
// =============================================================================
// Helpers
// =============================================================================
export function mapToolCallContent(c) {
    const text = extractToolCallText(c);
    switch (c.type) {
        case 'diff':
            return { type: 'diff', text, data: c };
        case 'terminal':
            return { type: 'terminal', text, data: c };
        case 'content':
        default:
            return { type: 'content', text, data: c };
    }
}
function isAgentCrashReport(value) {
    const record = maybeJsonRecord(value);
    return record !== null &&
        record.type === 'agent_crash_report' &&
        typeof record.agentType === 'string' &&
        typeof record.recovered === 'boolean' &&
        typeof record.message === 'string' &&
        typeof record.attribution === 'string' &&
        typeof record.stderrTruncated === 'boolean' &&
        typeof record.suggestion === 'string' &&
        typeof record.timestamp === 'string';
}
function extractToolCallText(value, depth = 0) {
    if (depth > 5 || value === null || value === undefined) {
        return '';
    }
    if (typeof value === 'string') {
        return value;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }
    if (Array.isArray(value)) {
        return value
            .map((entry) => extractToolCallText(entry, depth + 1))
            .map((entry) => entry.trim())
            .filter(Boolean)
            .join('\n');
    }
    if (typeof value !== 'object') {
        return '';
    }
    const record = expectJsonRecord(value, 'acp.tool_call_content');
    const preferredKeys = ['text', 'output', 'diff', 'content', 'stdout', 'stderr', 'message', 'result'];
    for (const key of preferredKeys) {
        const parsed = extractToolCallText(record[key], depth + 1).trim();
        if (parsed.length > 0) {
            return parsed;
        }
    }
    return '';
}
//# sourceMappingURL=useAcpMessages.js.map